import { useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { House, Calendar, BarChart3, Users, Target, Award, TrendingUp, History, Flame, Settings, Bell, UserRound } from 'lucide-react';
import {
  useLeagueData,
  useLeagueMutations,
  useViewedWeek,
  useViewedWeekRankings,
  useHasSubmittedPicks,
  useAwardsUnlockStatus,
  useAwardBallotSeasons,
  useSeasonConfig,
  useMemberApprovals,
  countPendingApprovals,
  useNotificationInbox,
  countUnread,
  useTakesBoard,
  useTakesSeen,
  useFranchiseProfiles,
  useMyFranchiseId
} from './hooks/queries/index.js';
import { arePickEmsOpen, areAwardsReleased } from './utils/seasonConfig.js';
import { useViewer } from './src/contexts/ViewerContext.jsx';
import { FranchiseAvatarsProvider } from './src/contexts/FranchiseAvatarsContext.jsx';
import { useAuth } from './src/contexts/AuthContext.jsx';
import { countUnseenTakes } from './src/components/takes/seen.js';
import { PROFILE_TAB } from './src/components/profile/profileLink.js';
import { viewableResultSeasons } from './src/components/awards/resultsAccess.js';
import { Card, CardContent } from './src/components/ui/card';
import { LoginDropdown } from './src/components/auth/LoginDropdown.jsx';
import { cn } from './src/lib/utils';
import ErrorBoundary from './utils/errorBoundary.jsx';

// Import global styles
import './globals.css';

// Components
// The landing tab is eager, so the first paint never waits on a chunk. The
// rankings table stays eager too: it is the next tab most people open.
import HomePage from './src/components/home/HomePage.jsx';
import PowerRankingsTable from './src/components/power-rankings/PowerRankingsTable.jsx';

import InlineWeekNavigator from './src/components/week-controls/InlineWeekNavigator.jsx';

import StandingsDrawer, { StandingsTrigger } from './src/components/standings/StandingsDrawer.jsx';
import { AlertsLink, HeaderNav, MobileTabBar, NewsletterLink, ProfileAvatarLink, SettingsLink } from './src/components/navigation/ResponsiveNavigation.jsx';
import PageContainer from './src/components/layout/PageContainer.jsx';
import LeagueChat, { ChatLauncher, LeagueChatOffset } from './src/components/ask/LeagueChat.jsx';
import { LeagueChatProvider } from './src/components/ask/LeagueChatContext.jsx';
import ApprovalPendingNotice from './src/components/auth/ApprovalPendingNotice.jsx';
import RouteLoading from './src/components/layout/RouteLoading.jsx';
import RankingsHeader from './src/components/power-rankings/RankingsHeader.jsx';
import { RANKINGS_VIEWS } from './src/components/power-rankings/rankingsViews.js';
import { ErrorFallback } from './utils/errorBoundary.jsx';

// One chunk per tab. Every tab, both app shells and recharts used to ship in
// the initial bundle; only the home page and the rankings table are eager now.
const StatisticsPanel = lazy(() => import('./src/components/dashboard/StatisticsPanel.jsx'));
const ScheduleManager = lazy(() => import('./src/components/schedule/ScheduleManager.jsx'));
const TeamsAndRosters = lazy(() => import('./src/components/teams/TeamsAndRosters.jsx'));
const PowerRankingsVisualization = lazy(() => import('./src/components/power-rankings/PowerRankingsVisualization.jsx'));
const PickEmsManager = lazy(() => import('./src/components/pickems/PickEmsManager.jsx'));
const AwardsManager = lazy(() => import('./src/components/awards/AwardsManager.jsx'));
const PlayoffsBracketManager = lazy(() => import('./src/components/playoffs/PlayoffsBracketManager.jsx'));
const LeagueHistoryManager = lazy(() => import('./src/components/history/LeagueHistoryManager.jsx'));
const TakesManager = lazy(() => import('./src/components/takes/TakesManager.jsx'));
const AlertsPage = lazy(() => import('./src/components/alerts/AlertsPage.jsx'));
const ProfilePage = lazy(() => import('./src/components/profile/ProfilePage.jsx'));
const UserSettingsPage = lazy(() =>
  import('./src/components/auth/UserSettingsPage.jsx').then((m) => ({ default: m.UserSettingsPage }))
);

/** The tab `/` resolves to. Also where an unknown or forbidden tab lands —
 *  which is why it is Home: it explains itself to whoever arrives (a
 *  directory and a sign-in line for a visitor), where a table did not. */
const DEFAULT_TAB = 'home';

/** Shown while a tab's chunk is in flight — see RouteLoading for why this is
 *  not a skeleton. Skeletons belong where the shape is known; a route-level
 *  fallback does not know what it is standing in for. */
const TabFallback = () => <RouteLoading />;

const FantasyFootballApp = () => {
  // Viewer identity and what they may see. `isTeamOwner` replaces an inline
  // `teamOwnerNames.includes(user.user_metadata.display_name)` that decided
  // History-tab access from the shell.
  //
  // `isApproved` is the members-only flag, not `isAuthenticated`: an account
  // the admin has not yet approved is signed in but sees what a visitor sees.
  const {
    user,
    isAuthenticated,
    isAuthLoading,
    isApproved,
    isApprovalLoading,
    isAdmin,
    isAdminLoading,
    isTeamOwner
  } = useViewer();
  const { signOut } = useAuth();

  // One query per thing, each with its own loading state. Replaces the
  // 60-callback mega-hook whose every mutation refetched the entire league.
  const {
    activeSeason,
    divisions,
    standings,
    rosters,
    rostersLoading,
    completedWeeks,
    isLoading,
    error
  } = useLeagueData();

  const seasonId = activeSeason?.id ?? null;
  const seasonConfig = useSeasonConfig();

  // `viewedWeek` is UI state the user owns; `actualWeek` is derived from the
  // season start date. Neither writes to the other — see hooks/queries/useWeek.
  const { viewedWeek, setViewedWeek, actualWeek } = useViewedWeek();

  const {
    addTeam,
    updateTeam,
    removeTeam,
    addGame,
    createDivision,
    renameDivision,
    deleteDivision,
    assignTeamToDivision
  } = useLeagueMutations(seasonId);

  const { data: weeklyRankings = [], isPending: rankingsLoading } =
    useViewedWeekRankings(seasonId);

  // The nav dot asks "have you submitted *this week's* picks", so it follows the
  // actual week. Browsing back to week 3 must not light it up. The pick'ems tab
  // itself still follows `viewedWeek`.
  const { hasSubmitted: hasUserSubmittedPicks, isLoading: pickemNotificationLoading } =
    useHasSubmittedPicks(seasonId, actualWeek, { enabled: isApproved && Boolean(user) });

  const { status: awardsUnlockStatus } = useAwardsUnlockStatus(seasonId);

  // The badge on the cog: accounts waiting on the admin. Settings is not a
  // tab in either nav, so it has no notification dot of its own, and Approvals
  // is a panel inside it — a member who signed up stayed invisible until the
  // admin went looking. Same query key the Approvals panel reads, so opening
  // it costs nothing extra, and disabled for everyone else.
  const { data: approvalRows = [] } = useMemberApprovals({ enabled: isAdmin });
  const pendingApprovals = isAdmin ? countPendingApprovals(approvalRows) : 0;

  // The bell's badge: the viewer's unread alerts, for every signed-in viewer.
  // Same cache entry the Alerts page reads, so the number and the list agree.
  const { data: alertRows = [] } = useNotificationInbox({ enabled: isAuthenticated });
  const unreadAlerts = isAuthenticated ? countUnread(alertRows) : 0;

  // The badge on the Takes tab: predictions posted since this viewer last
  // opened the board. Takes is the one tab whose contents arrive without the
  // viewer doing anything — somebody else calls something, and nothing on any
  // other page says so — which is why it gets a count rather than the dot
  // Pick'ems carries: that dot means "you owe something", this means "there is
  // something to read", and the two are answered differently.
  //
  // The query is the tab's own cache entry (`qk.takes.board`), borrowed: a
  // member who sees the badge has already fetched what the tab will render, so
  // opening Takes costs no request. Both are disabled for anyone who may not
  // read the board, for whom RLS returns nothing anyway.
  //
  // The mark is a `take_views` row, so it follows the member rather than the
  // browser, and the tab's write lands in this same cache entry — which is the
  // whole reason the badge clears here when the board is read over there.
  const { board: takesBoard } = useTakesBoard(seasonId, { enabled: isApproved });
  const { lastSeenAt: takesLastSeenAt, isMarkLoading: takesMarkLoading } = useTakesSeen(
    user?.id,
    { enabled: isApproved }
  );

  // Nothing until the mark has arrived. A null mark means "never looked",
  // which is the whole board unread — indistinguishable from a fetch still in
  // flight, and the difference between the badge being right and it flashing
  // "9+" on every cold load before settling to nothing.
  const unseenTakes = useMemo(
    () =>
      isApproved && !takesMarkLoading
        ? countUnseenTakes(takesBoard.takes, takesLastSeenAt, user?.id)
        : 0,
    [isApproved, takesMarkLoading, takesBoard, takesLastSeenAt, user]
  );

  // Awards outlive the season that produced them. The tab used to be gated
  // solely on the *active* season's release date and voting flag — both off for
  // a season that has not run its ballot yet — which sealed the tab over every
  // past season's results, for everyone but the admin.
  const { data: ballotSeasons, isPending: ballotSeasonsLoading } = useAwardBallotSeasons();
  const hasViewableAwardResults = useMemo(
    () =>
      viewableResultSeasons(ballotSeasons, {
        isAdmin,
        activeSeasonResultsReleased: Boolean(awardsUnlockStatus?.resultsReleased)
      }).length > 0,
    [ballotSeasons, isAdmin, awardsUnlockStatus]
  );

  // The tab is the URL, not component state. It used to be `useState`, which
  // meant the phone's back button left the site instead of walking back a tab,
  // and no tab could be linked to or survive a refresh.
  const navigate = useNavigate();
  const { tab, franchiseId: profileFranchiseId } = useParams();
  const { data: franchiseProfiles } = useFranchiseProfiles();
  const { data: myFranchiseId } = useMyFranchiseId();
  const viewerAvatarUrl = franchiseProfiles?.[myFranchiseId]?.avatarUrl ?? null;
  const activeTab = tab || (profileFranchiseId ? PROFILE_TAB : DEFAULT_TAB);
  const setActiveTab = useCallback((id) => navigate(`/${id}`), [navigate]);

  // The Statistics tab's face — the rankings table, the analysis charts or the
  // league statistics — is in the URL (`?view=`), so the phone's back button
  // walks back through it and `/statistics`, the tab it replaced, can land on
  // it (App.jsx redirects there).
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const rankingsView = RANKINGS_VIEWS.includes(requestedView) ? requestedView : 'table';
  const setRankingsView = useCallback(
    (view) => setSearchParams(view === 'table' ? {} : { view }),
    [setSearchParams]
  );
  const [standingsOpen, setStandingsOpen] = useState(false);
  const openStandings = useCallback(() => setStandingsOpen(true), []);

  // Check if awards are accessible
  const isAwardsAccessible = () => {
    // Admins always have access
    if (isAdmin) return true;

    // Check if voting is open to all approved members
    if (isApproved && awardsUnlockStatus?.votingOpenToAll) return true;

    // A past season that was voted on is reachable on its own account — see
    // `src/components/awards/resultsAccess.js`, which the Results tab's picker
    // reads too so the two cannot disagree about what is in there.
    if (hasViewableAwardResults) return true;

    // Otherwise the season's own release date decides.
    return areAwardsReleased(seasonConfig);
  };

  // Check if pickems are still open. The window comes from the season row
  // (open Tuesday 04:00, close Thursday 20:00 in the league's time zone),
  // not from a weekday/hour rule duplicated in the view layer.
  const arePickemsOpen = () => arePickEmsOpen(seasonConfig, actualWeek);

  // Main navigation tabs - use useMemo to recalculate when dependencies change
  const mainTabs = useMemo(() => {
    const awardsAccessible = isAwardsAccessible();

    return [
      // `shortLabel` is what a narrow nav shows in place of `label`. Teams
      // carries its short name at every width; "Power Rankings" and "Teams &
      // Rosters" were dropped on 2026-09-10 because the nav is not where the
      // page explains itself.
      //
      // `tabBarSlot` puts a tab in the phone tab bar, in that position; every
      // other tab the viewer may open is in the bar's Menu. Four slots and
      // Menu make five, which fit a 375px screen without scrolling.
      //
      // Home is everyone's — the page itself decides its shape (a directory
      // for visitors, the dashboard for members). `weekAware: false` hides the
      // header's week control while it is open: Home is present tense, and a
      // control that changes nothing on the page it sits on is one the reader
      // will press and then distrust. A flag on the definition rather than an
      // `activeTab === 'home'` in the header, so another present-tense tab can
      // opt out the same way.
      { id: 'home', label: 'Home', icon: House, requiresSeason: false, requiresAuth: false, weekAware: 'desktop', tabBarSlot: 0 },
      // Rankings and Statistics are one tab since 2026-10-23: the rankings
      // table, with Analysis and Statistics as views beside it
      // (`RankingsHeader`). Its id stays `rankings` so every existing link
      // and bookmark still lands; `/statistics` redirects to the view.
      { id: 'rankings', label: 'Statistics', icon: BarChart3, requiresSeason: true, requiresAuth: false, tabBarSlot: 1 },
      { id: 'schedule', label: 'Schedule', icon: Calendar, requiresSeason: true, requiresAuth: false },
      { id: 'teams', label: 'Teams', icon: Users, requiresSeason: true, requiresAuth: false },
      // The admin is let through explicitly, the way every `getMasked*` helper
      // already treats them — owning a team is not a prerequisite for running
      // the league.
      { id: 'history', label: 'History', icon: History, requiresSeason: false, requiresAuth: false, customAccess: isAdmin || isTeamOwner, tabBarSlot: 3 },
      // Pick'ems, Playoffs and Awards are for approved members, like Takes:
      // signed-out and unapproved viewers do not see them at all. This is the
      // shell's gate only — the tables behind them stay public-read.
      { id: 'pickems', label: 'Pick\'ems', icon: Target, requiresSeason: true, requiresAuth: false, customAccess: isApproved, tabBarSlot: 2 },
      // Members only — approved members, not merely signed-in ones; the
      // board's RLS asks the same question. `requiresAuth` is *not* the flag
      // for this — despite the name it means admin-only (`requiresAuth &&
      // !isAdmin`), which would hide the board from the fourteen people it is
      // for. `customAccess` is how History already expresses a non-admin
      // audience.
      { id: 'takes', label: 'Takes', icon: Flame, requiresSeason: true, requiresAuth: false, customAccess: isApproved },
      { id: 'playoffs', label: 'Playoffs', icon: TrendingUp, requiresSeason: true, requiresAuth: false, customAccess: isApproved },
      // The league-wide TD parlay board is not a destination of its own. It
      // lives inside Pick'ems as a tab beside the form the picks it reports
      // on are entered in, open to everyone who can open Pick'ems — so its
      // audience is this tab's audience, and it needs no entry here.
      // Awards: approved members only, and then the release rules above
      // still decide.
      { id: 'awards', label: 'Awards', icon: Award, requiresSeason: true, requiresAuth: false, customAccess: isApproved && awardsAccessible },
      // Settings is a tab — same route table, same guard, same shell — but
      // not a nav item: `inNav: false` keeps it out of the header list and
      // the phone tab bar. From lg the cog beside the account control
      // (`SettingsLink`) opens it; below lg, the phone tab bar's Menu does.
      // It used to be a separate
      // page mounted outside the shell, with its own header and a Back
      // button; it acts like every other tab now. Any signed-in account,
      // approved or not: Profile (name, password) is theirs regardless.
      { id: 'settings', label: 'Settings', icon: Settings, requiresSeason: false, requiresAuth: false, customAccess: isAuthenticated, inNav: false },
      // Every notification the viewer was sent. Reached by the bell in the
      // header, which is on screen at every width — not a nav tab, so it is
      // never one tap further away inside the phone tab bar's Menu.
      { id: 'alerts', label: 'Alerts', icon: Bell, requiresSeason: false, requiresAuth: false, customAccess: isAuthenticated, inNav: false },
      // A franchise's page, opened by every team name and by the Menu. Members
      // only, like Takes. The header's week control stays, as on Home, and
      // changes nothing here: the page is present tense, and its week-by-week
      // section has its own pickers.
      { id: PROFILE_TAB, label: 'Profile', icon: UserRound, requiresSeason: false, requiresAuth: false, customAccess: isApproved, inNav: false }
    ];
  }, [isApproved, isAdmin, awardsUnlockStatus, user, isTeamOwner, seasonConfig, hasViewableAwardResults, isAuthenticated]);

  // One definition of "may this viewer see this tab", shared by the nav and by
  // the route guard below — they must not be able to disagree.
  const shouldShowTab = useCallback((t) => {
    if (t.requiresAuth && !isAdmin) return false;
    if (t.customAccess !== undefined) {
      const hasAccess = typeof t.customAccess === 'function' ? t.customAccess() : t.customAccess;
      if (!hasAccess) return false;
    }
    return true;
  }, [isAdmin]);

  const activeTabDef = mainTabs.find((t) => t.id === activeTab);
  // `true` (the default), `false`, or `'desktop'`: Home is present tense and
  // shows no week on a phone, where the header has no room to spare, but keeps
  // the control from lg as the way into a week.
  const weekControl = activeTabDef?.weekAware ?? true;

  // What the nav actually renders: the tab list plus the one piece of state
  // that is the shell's to know — whether a tab is waiting on the viewer.
  //
  // Nothing is disabled for a missing season any more. That rule used to read
  // `isAdmin && tab.requiresSeason && !activeSeason`, so the admin got greyed-
  // out tabs while everyone else got working ones — the inversion of what was
  // presumably meant, and either way it contradicts the design directly above:
  // every tab renders its own "no season yet" state, which explains the
  // situation, where a dead nav item explains nothing.
  const settingsBadgeLabel = `${pendingApprovals} ${pendingApprovals === 1 ? 'account' : 'accounts'} awaiting approval`;

  const needsPicks =
    isApproved && !hasUserSubmittedPicks && !pickemNotificationLoading && arePickemsOpen();
  const navTabs = useMemo(
    () =>
      mainTabs
        .filter((tab) => tab.inNav !== false)
        .map((tab) => ({
          ...tab,
          showNotification: tab.id === 'pickems' && needsPicks,
          badgeCount: tab.id === 'takes' ? unseenTakes : 0,
        })),
    [mainTabs, needsPicks, unseenTakes]
  );

  // Mutations arrive as TanStack objects; the tab components still take plain
  // callbacks, so adapt at the boundary rather than rewriting every child.
  const handleGameUpdate = (week, team1Id, team2Id, team1Score, team2Score) =>
    addGame.mutateAsync({ week, team1Id, team2Id, team1Score, team2Score });

  const handleAddTeam = (name, owner) => addTeam.mutateAsync({ name, owner });
  const handleUpdateTeam = (teamId, updates) => updateTeam.mutateAsync({ teamId, updates });
  const handleRemoveTeam = (teamId) => removeTeam.mutateAsync(teamId);
  const handleCreateDivision = (name, displayOrder) =>
    createDivision.mutateAsync({ name, displayOrder });
  const handleRenameDivision = (divisionId, name) =>
    renameDivision.mutateAsync({ divisionId, name });
  const handleDeleteDivision = (divisionId) => deleteDivision.mutateAsync(divisionId);
  const handleTeamDivisionChange = (teamId, divisionId) =>
    assignTeamToDivision.mutateAsync({ teamId, divisionId });


  // Note: Allow users to stay on any tab even without an active season
  // Each tab will show appropriate "no season available" messages

  // A URL naming a tab that does not exist is wrong right away. A URL naming a
  // tab this viewer may not open is only knowable once the league data that
  // access depends on has arrived — redirecting before then would bounce a
  // legitimate deep link to /awards or /history on every cold load.
  //
  // The session is the other half of that answer, and it resolves on its own
  // schedule: `isAuthenticated` reads false while a stored session is still
  // being read back, which is indistinguishable from signed out. Waiting for
  // `isAuthLoading` is what lets a member's bookmarked /takes survive a cold
  // load — and it fixes the same latent bounce on /history, whose access runs
  // through `isTeamOwner` and so was already exposed to it.
  //
  // The ballot-season list is the third such input, and for the same reason:
  // while it is in flight `hasViewableAwardResults` is false, which is
  // indistinguishable from "no past season has results" — so a bookmarked
  // /awards would bounce on every cold load without this.
  //
  // And the approval is the fourth: `isApproved` reads false until the row
  // has been fetched, which is indistinguishable from "not approved", so a
  // member's bookmarked /takes would bounce the same way.
  //
  // And admin is the fifth: it is a `league_admins` row asked of the
  // database, not a build-time id, so `isAdmin` reads false until the RPC
  // answers. History (`isAdmin || isTeamOwner`) and Awards
  // (`isAwardsAccessible`) are gated on it, and an admin's bookmarked
  // /history would bounce without this. (`isApprovalLoading` also waits on the
  // admin check, but the guard names the flag it depends on.)
  if (!activeTabDef) return <Navigate to={`/${DEFAULT_TAB}`} replace />;
  if (
    !isLoading &&
    !isAuthLoading &&
    !isApprovalLoading &&
    !isAdminLoading &&
    !ballotSeasonsLoading &&
    !shouldShowTab(activeTabDef)
  ) {
    return <Navigate to={`/${DEFAULT_TAB}`} replace />;
  }

  return (
    <ErrorBoundary>
      <FranchiseAvatarsProvider>
      <LeagueChatProvider enabled={isApproved}>
      <LeagueChatOffset className="min-h-screen bg-background">
        {/* Header - Responsive Design */}
        <header className="sticky top-0 z-50 w-full border-b bg-card/80 backdrop-blur-sm">
          <PageContainer>
            {/* Not `justify-between`.
                That pushed the two groups to opposite edges and parked all the
                slack in one visible void between the standings button and the
                first nav item — 159px of it at 1536. The row is a single flex
                line with one gap now: navigation sits directly after the
                controls it belongs with, and the leftover space collects in
                front of the account control, in the corner, where it reads as
                margin rather than as a hole. */}
            <div className="flex h-14 items-center gap-2 sm:h-16 sm:gap-3">
              {/* LEFT — the constant side: who and when.
                  Identity, the week being viewed, and the standings. These are
                  the controls that are on every page and mean the same thing
                  on every page, so they hold one position the eye can learn.
                  Keeping them together also frees the right-hand side to grow:
                  navigation is what changes as the app gains tabs, and it can
                  do that without shunting the week control around.

                  The season name reads at every width — it used to be the
                  first thing sacrificed to make room for the hamburger, so a
                  phone user could not tell which season they were looking at.
                  Below lg the brand *may* shrink: there is no nav on this row
                  to protect, and a signed-out "Login" button is wider than a
                  signed-in avatar, which is what once pushed a 375px header
                  3px past the viewport. Truncating the league name is the
                  right thing to give up there. */}
              <div className="flex min-w-0 shrink items-center gap-2 sm:gap-3 lg:shrink-0">
                {/* The brand is the way home: one link around the logo, the
                    wordmark and the season line — one hit area, at least
                    44px tall. It is the only route back from Settings and
                    Alerts, which have no tab of their own, and it is what
                    every member already expects a logo to do. */}
                <Link
                  to="/home"
                  aria-label="og jits home"
                  aria-current={activeTab === 'home' ? 'page' : undefined}
                  className="group -mx-1 flex min-h-11 min-w-0 items-center gap-2 rounded-lg px-1 no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring sm:gap-3"
                >
                  <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg transition-transform group-hover:scale-[1.02]">
                    <img src="og jits logo.jpg" alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="truncate font-display text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
                      og jits
                    </h1>
                    {activeSeason && (
                      <p className="truncate text-xs leading-tight text-muted-foreground">
                        {activeSeason.name || `${activeSeason.year} Season`}
                      </p>
                    )}
                  </div>
                </Link>

                {activeSeason && (
                  <>
                    {/* A hairline between the brand and the controls, so the
                        two read as identity *and* tools rather than as one
                        undifferentiated cluster. */}
                    <span className="hidden h-6 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
                    {/* Absent on a present-tense tab (`weekAware: false`), and
                        below lg on Home (`'desktop'`). The standings stay: they
                        mean the same on every page. */}
                    {weekControl && (
                      <div className={weekControl === 'desktop' ? 'hidden lg:block' : 'contents'}>
                        <InlineWeekNavigator
                          currentWeek={viewedWeek}
                          totalWeeks={activeSeason.totalWeeks}
                          regularSeasonWeeks={activeSeason.regularSeasonWeeks}
                          onWeekChange={setViewedWeek}
                          completedWeeks={completedWeeks}
                          season={activeSeason}
                        />
                      </div>
                    )}
                    <StandingsTrigger onClick={() => setStandingsOpen(true)} />
                  </>
                )}
              </div>

              {/* RIGHT — the side that grows: where to go, and who you are.
                  Navigation lives here because it is the part of the header
                  that gains items over time; anything new belongs on this
                  side, where it pushes against the account control rather
                  than against the week. */}
              {/* The nav takes the remaining width and lays its items out from
                  the left, so the distance from the standings button to the
                  first tab is the row's own gap and nothing more. Growing the
                  tab list consumes this space outwards instead of re-centring
                  everything; when it runs out, the nav scrolls rather than
                  pushing the page wider. */}
              <div className="flex min-w-0 flex-1 items-center">
                <HeaderNav
                  tabs={navTabs}
                  activeTab={activeTab}
                  shouldShowTab={shouldShowTab}
                />
              </div>

              {/* The bell stays at every width. From lg the newsletter, the
                  cog and the avatar sit beside it; below lg those three are
                  in the phone tab bar's Menu, and the header keeps only the
                  bell — and Login, for a signed-out viewer, which is never in
                  the menu. Signed out there is nothing to read or set. The
                  newsletter is for approved members, like the members-only
                  tabs. */}
              <div className="flex shrink-0 items-center gap-1">
                {isApproved && <NewsletterLink className="hidden lg:flex" />}
                {isAuthenticated && (
                  <AlertsLink active={activeTab === 'alerts'} badgeCount={unreadAlerts} />
                )}
                {isAuthenticated && (
                  <SettingsLink
                    className="hidden lg:flex"
                    active={activeTab === 'settings'}
                    badgeCount={pendingApprovals}
                    badgeLabel={settingsBadgeLabel}
                  />
                )}
                <div className={cn(user && 'hidden lg:block')}>
                  {/* A member's avatar goes straight to their profile, which
                      is where Log out lives. Anyone else has no profile, so
                      they keep the dropdown. */}
                  {isApproved ? (
                    <ProfileAvatarLink
                      user={user}
                      avatarUrl={viewerAvatarUrl}
                      active={activeTab === PROFILE_TAB && profileFranchiseId === myFranchiseId}
                    />
                  ) : (
                    <LoginDropdown />
                  )}
                </div>
              </div>
            </div>
          </PageContainer>
        </header>

        {/* Main content.
            The bottom padding clears the phone tab bar and the chat launcher
            floating above it: both are `fixed`, so neither occupies layout
            space, and without this the last row of every page sits under
            one of them. The launcher is 3rem tall at 4.75rem up, so the
            page ends 8.5rem up. `pb-safe` handles the home indicator below
            that. From `lg` the launcher sits in the corner, clear of the
            column. */}
        <PageContainer
          as="main"
          className="py-6 pb-[calc(8.5rem+env(safe-area-inset-bottom))] sm:py-8 sm:pb-[calc(9rem+env(safe-area-inset-bottom))] lg:py-10 lg:pb-16"
        >
          {/* Signed in but not yet approved by the admin: says so, once, at the
              top of every page. Renders nothing for everyone else. */}
          <ApprovalPendingNotice className="mb-6" />

          {/* Error Display */}
          {/* `{error}` — not `{error.message}` — was rendering an Error object
              as a React child, which is React error #31 and takes down the
              whole tree. So the one branch that exists to *report* a failed
              load was itself the thing that turned a failed load into
              "Something went wrong", with the real message lost. Any data
              error did this; it needed no exotic conditions, only a request
              that did not succeed. */}
          {error && (
            <Card className="mb-6 border-destructive">
              <CardContent className="p-4">
                <p className="text-destructive">
                  {error instanceof Error ? error.message : String(error)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Tab Content. One Suspense boundary covers every lazy tab; the
              landing tab is eager so the first paint needs no chunk. */}
          <div className="space-y-6">
            <Suspense fallback={<TabFallback />}>
            {activeTab === 'home' && (
              <ErrorBoundary key="home-error-boundary">
                <HomePage
                  tabs={navTabs}
                  shouldShowTab={shouldShowTab}
                  onOpenStandings={openStandings}
                />
              </ErrorBoundary>
            )}

            {activeTab === 'rankings' && (
              <ErrorBoundary key="rankings-error-boundary">
                {/* The header names the viewed week, and the viewed week is
                    seeded from the season row — until that row arrives,
                    `useActualWeek()` answers 1 and the title read "Week 1
                    Power Rankings" for a paint before snapping to the real
                    week. The table already showed this spinner for the same
                    interval, so the header waits with it: one spinner, then
                    the right week, nothing in between. */}
                {isLoading ? (
                  <RouteLoading />
                ) : (
                  <div>
                    <RankingsHeader
                      week={viewedWeek}
                      view={rankingsView}
                      onViewChange={setRankingsView}
                    />
                    {rankingsView === 'table' && (
                      <PowerRankingsTable
                        rankings={weeklyRankings}
                        currentWeek={viewedWeek}
                        showFaab={viewedWeek === actualWeek}
                        loading={rankingsLoading}
                        initializing={isLoading}
                      />
                    )}
                    {rankingsView === 'analysis' && (
                      <PowerRankingsVisualization
                        rankings={weeklyRankings}
                        currentWeek={viewedWeek}
                        loading={rankingsLoading}
                      />
                    )}
                    {rankingsView === 'statistics' && (
                      <StatisticsPanel
                        embedded
                        rankings={weeklyRankings}
                        currentWeek={viewedWeek}
                        season={activeSeason}
                        loading={rankingsLoading}
                      />
                    )}
                  </div>
                )}
              </ErrorBoundary>
            )}


            {activeTab === 'schedule' && (
              <ErrorBoundary key="schedule-error-boundary">
                <div className="space-y-6">
                  <ScheduleManager
                    season={activeSeason}
                    schedule={activeSeason?.schedule || []}
                    currentWeek={viewedWeek}
                    onUpdateGame={isAdmin ? handleGameUpdate : null}
                    onDeleteGame={null}
                    onWeekChange={setViewedWeek}
                    loading={isLoading}
                    isAuthenticated={isAdmin}
                    powerRankings={weeklyRankings}
                    rosters={rosters}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'teams' && (
              <ErrorBoundary key="teams-error-boundary">
                <div className="space-y-6">
                  <TeamsAndRosters
                    season={activeSeason}
                    teams={activeSeason?.teams || []}
                    rosters={rosters}
                    rostersLoading={rostersLoading}
                    onAddTeam={isAdmin ? handleAddTeam : null}
                    onUpdateTeam={isAdmin ? handleUpdateTeam : null}
                    onRemoveTeam={isAdmin ? handleRemoveTeam : null}
                    loading={isLoading}
                    powerRankings={weeklyRankings}
                    isAuthenticated={isAdmin}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'pickems' && (
              <ErrorBoundary key="pickems-error-boundary">
                <div className="space-y-6">
                  {/* `isAuthenticated` on the tab components means "may this
                      viewer submit", and that is approval now — the RPCs and
                      policies behind every form ask `is_approved_member()`. */}
                  <PickEmsManager
                    season={activeSeason}
                    currentWeek={viewedWeek}
                    loading={isLoading}
                    isAuthenticated={isApproved}
                    initializing={isLoading}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'takes' && (
              <ErrorBoundary key="takes-error-boundary">
                <div className="space-y-6">
                  <TakesManager
                    season={activeSeason}
                    currentWeek={viewedWeek}
                    /* Auth counts as loading here. Until the session resolves
                       the page cannot tell a member from a visitor, and the
                       honest answer to "who is this" is not yet, rather than a
                       board that flashes its signed-out state and then flips. */
                    loading={isLoading || isAuthLoading || isApprovalLoading}
                    isAuthenticated={isApproved}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'awards' && (
              <ErrorBoundary key="awards-error-boundary">
                <div className="space-y-6">
                  <AwardsManager
                    season={activeSeason}
                    currentWeek={viewedWeek}
                    loading={isLoading}
                    isAuthenticated={isApproved}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'playoffs' && (
              <ErrorBoundary key="playoffs-error-boundary">
                <div className="space-y-6">
                  <PlayoffsBracketManager
                    season={activeSeason}
                    currentWeek={viewedWeek}
                    loading={isLoading}
                    isAuthenticated={isApproved}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'history' && (
              <ErrorBoundary key="history-error-boundary">
                <div className="space-y-6">
                  <LeagueHistoryManager
                    activeSeason={activeSeason}
                  />
                </div>
              </ErrorBoundary>
            )}

            {activeTab === 'settings' && (
              <ErrorBoundary key="settings-error-boundary">
                <UserSettingsPage />
              </ErrorBoundary>
            )}

            {activeTab === 'alerts' && (
              <ErrorBoundary key="alerts-error-boundary">
                <AlertsPage />
              </ErrorBoundary>
            )}

            {activeTab === PROFILE_TAB && (
              <ErrorBoundary key="profile-error-boundary">
                <ProfilePage />
              </ErrorBoundary>
            )}

            </Suspense>
          </div>
        </PageContainer>

        {/* The phone tab bar is a sibling of the header, not a child of it.
            The header carries `backdrop-blur`, and backdrop-filter makes an
            element the containing block for `position: fixed` descendants —
            nested inside it, `bottom-0` resolved against the header and the
            bar rendered directly beneath it at the top of the screen. */}
        <MobileTabBar
          tabs={navTabs}
          activeTab={activeTab}
          shouldShowTab={shouldShowTab}
          account={{
            user: isAuthenticated ? user : null,
            showNewsletter: isApproved,
            showProfile: isApproved,
            avatarUrl: viewerAvatarUrl,
            settingsBadgeCount: pendingApprovals,
            settingsBadgeLabel,
            onSignOut: signOut
          }}
        />

        {/* Ask og jits: the round launcher below lg, and the drawer or side
            panel it and the header button open, for approved members. At the
            root for the same reason as the tab bar. */}
        <ChatLauncher />
        <LeagueChat />

        {/* Standings Drawer */}
        {activeSeason && (
          <StandingsDrawer
            open={standingsOpen}
            onOpenChange={setStandingsOpen}
            seasonId={activeSeason.id}
            teams={activeSeason.teams || []}
            divisions={divisions}
            standings={standings}
            currentWeek={viewedWeek}
            seasonYear={activeSeason.year}
            loading={isLoading}
            isAuthenticated={isAdmin}
            onDivisionRename={handleRenameDivision}
            onTeamDivisionChange={handleTeamDivisionChange}
            onCreateDivision={handleCreateDivision}
            onDivisionDelete={handleDeleteDivision}
            games={activeSeason.schedule || []}
          />
        )}

        {/*
          There is no full-screen loading overlay any more.
          It was already halfway to being removed: a previous pass narrowed it
          from "any loading" to "first load with no season", because every
          mutation used to black out the page behind a modal. The remaining
          case was still wrong, and CI proved it — a fixed `inset-0` panel at
          z-50 sits *above* the z-40 tab bar, so whenever the league data does
          not arrive the app is not merely blank, it is unusable: navigation is
          covered, and no tab can be reached to see what else works.

          "Does not arrive" is not hypothetical. It is a dropped connection, an
          outage, a bad anon key — and in those cases the shell, the nav and
          every page's own empty state are exactly what the reader needs. Each
          surface already owns its loading state (RouteLoading, per-page empty
          states), so nothing here needs a modal on top of them.
        */}
      </LeagueChatOffset>
      </LeagueChatProvider>
      </FranchiseAvatarsProvider>
    </ErrorBoundary>
  );
};


export default FantasyFootballApp;